import {
  HorizontalConnectionPos,
  Overlay,
  OverlayRef,
  PositionStrategy,
  VerticalConnectionPos,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { TooltipComponent } from './tooltip.component';

@Directive({
    selector: '[glTooltip]',
    standalone: false
})
export class TooltipDirective implements OnInit, OnDestroy, OnChanges {
  @Input() private glTooltip: string;
  @Input() private offset: number;
  @Input() private position: 'bottom' | 'right' | 'left' | 'top';
  @Input() private isError: boolean;
  @Input() private disableTooltip: boolean;
  @Input() private messageValue: number;
  @Input() private width: string;
  private tooltipTimeout: number;
  private portal: ComponentPortal<TooltipComponent>;
  private tooltipInstance: TooltipComponent;
  private overlayRef: OverlayRef;

  constructor(private elRef: ElementRef<HTMLElement>, private overlay: Overlay) {}

  public ngOnInit(): void {
    if (!this.offset) {
      this.offset = 10;
    }
    if (!this.position) {
      this.position = 'top';
    }
  }

  public ngOnDestroy(): void {
    if (this.tooltipTimeout) {
      window.clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = undefined;
    } else {
      this.destroyTooltipComponent();
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes['disableTooltip']) {
      this.destroyTooltipComponent();
    }
    if (changes['glTooltip'] && this.tooltipInstance?.message) {
    this.tooltipInstance.message = this.glTooltip;
    }
  }

  @HostListener('mouseenter') public onMouseEnter() {
    this.tooltipTimeout = window.setTimeout(() => {
      if (!this.disableTooltip) {
        this.generateTooltipComponent();
      }
      this.tooltipTimeout = undefined;
    }, 200);
  }

  @HostListener('mouseleave') public onMouseLeave() {
    if (this.tooltipTimeout) {
      window.clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = undefined;
    } else {
      this.destroyTooltipComponent();
    }
  }

  private generateTooltipComponent() {
    this.overlayRef = this.overlay.create({
      positionStrategy: this.generatePositionStrategy(),
    });
    this.portal = new ComponentPortal(TooltipComponent);
    this.tooltipInstance = this.overlayRef.attach(this.portal).instance;
    this.tooltipInstance.hookEl = this.elRef;
    this.tooltipInstance.isError = this.isError;
    this.tooltipInstance.message = this.glTooltip;
    this.tooltipInstance.side = this.position;
    this.tooltipInstance.messageValue = this.messageValue;
    this.tooltipInstance.width = this.width;
    this.overlayRef.updatePosition();
    this.overlayRef.overlayElement.style.pointerEvents = 'none';
  }

  private destroyTooltipComponent() {
    if (this.overlayRef) {
      this.overlayRef.detach();
    }
  }

  private generatePosition(
    originX: HorizontalConnectionPos,
    originY: VerticalConnectionPos,
    overlayX: HorizontalConnectionPos,
    overlayY: VerticalConnectionPos,
  ) {
    return this.overlay
      .position()
      .flexibleConnectedTo(this.elRef)
      .withPositions([
        {
          originX,
          originY,
          overlayX,
          overlayY,
        },
      ])
      .withViewportMargin(10);
  }

  private generatePositionStrategy(): PositionStrategy {
    let overlayConfig;
    if (this.position === 'bottom') {
      overlayConfig = this.generatePosition('center', 'top', 'center', 'top').withDefaultOffsetY(
        this.elRef.nativeElement.clientHeight + this.offset,
      );
    } else if (this.position === 'top') {
      overlayConfig = this.generatePosition(
        'center',
        'bottom',
        'center',
        'bottom',
      ).withDefaultOffsetY(-this.elRef.nativeElement.clientHeight - this.offset);
    } else if (this.position === 'left') {
      overlayConfig = this.generatePosition('end', 'center', 'end', 'center').withDefaultOffsetX(
        -this.elRef.nativeElement.clientWidth - this.offset,
      );
    } else if (this.position === 'right') {
      overlayConfig = this.generatePosition(
        'start',
        'center',
        'start',
        'center',
      ).withDefaultOffsetX(this.elRef.nativeElement.clientWidth + this.offset);
    }
    return overlayConfig as PositionStrategy;
  }
}
